import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import ChallengeCard from './ChallengeCard';

const ActiveChallengesSection = () => {
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const response = await fetch("https://dev.100protocol.com/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: `
              query MyQuery {
                fetchActiveChallenges {
                  deadline
                  coverImg
                  reward_amount_usd
                  id
                  title
                  status
                }
              }
            `,
          }),
        });

        const data = await response.json();
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const filteredChallenges = data.data.fetchActiveChallenges.filter(
          (challenge) =>
            challenge.status === 0 &&
            challenge.deadline > currentTimestamp
        );
        setChallenges(filteredChallenges);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchChallenges();
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className={`relative py-24 px-4 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'} overflow-hidden`}>
        <div className="max-w-[1300px] mx-auto relative z-10">
          <h2 className={`text-[24px] text-left md:text-[32px] lg:text-[36px] font-bold ${isDarkMode ? 'text-white' : 'text-black'} mb-12`}>
            Active Challenges
          </h2>
          <div className="flex gap-5">
            {[1].map((i) => (
              <div 
                key={i}
                className={`w-[285px] flex-shrink-0 rounded-[30px] overflow-hidden ${isDarkMode ? 'bg-[#1A1A1A]' : 'bg-gray-100'}`}
              >
                {/* Image skeleton */}
                <div className={`w-full h-[160px] ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} animate-pulse`} />
                
                {/* Content container */}
                <div className="p-6">
                  {/* Title skeleton */}
                  <div className={`h-6 ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} rounded-full w-3/4 mb-4 animate-pulse`} />
                  
                  {/* Amount skeleton */}
                  <div className={`h-8 ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} rounded-full w-1/2 mb-6 animate-pulse`} />
                  
                  {/* Deadline skeleton */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className={`w-4 h-4 rounded-full ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} animate-pulse`} />
                    <div className={`h-4 ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} rounded-full w-1/3 animate-pulse`} />
                  </div>
                  
                  {/* Button skeleton */}
                  <div className={`h-[50px] ${isDarkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'} rounded-full w-full animate-pulse`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className={`relative py-24 px-4 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'} overflow-hidden`}>
        <div className="max-w-[1300px] mx-auto relative z-10">
          <h2 className={`text-[24px] md:text-[32px] lg:text-[36px] font-bold ${isDarkMode ? 'text-white' : 'text-black'} mb-12`}>
            Active Challenges
          </h2>
          <p className={`text-center ${isDarkMode ? 'text-white' : 'text-black'}`}>
            Failed to load challenges. Please try again later.
          </p>
        </div>
      </section>
    );
  }

  // No challenges state
  if (!challenges || challenges.length === 0) {
    return (
      <section className={`relative py-24 px-4 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'} overflow-hidden`}>
        <div className="max-w-[1300px] mx-auto relative z-10">
          <h2 className={`text-[24px] md:text-[32px] lg:text-[36px] font-bold ${isDarkMode ? 'text-white' : 'text-black'} mb-12`}>
            Active Challenges
          </h2>
          <p className={`text-center ${isDarkMode ? 'text-white' : 'text-black'}`}>
            No active challenges at the moment.
          </p>
        </div>
      </section>
    );
  }

  const scrollToIndex = (index) => {
    if (sliderRef.current) {
      const cardWidth = 320;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const nextSlide = () => {
    const nextIndex = Math.min(currentIndex + 1, challenges.length - 1);
    scrollToIndex(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    scrollToIndex(prevIndex);
  };

  return (
    <section className={`relative py-16 px-4 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`}>
      <div className="max-w-[1300px] mx-auto">
        <div className="mb-8 md:mb-10 lg:mb-12">
          <motion.h2 
            className={`text-[28px] md:text-[36px] font-bold ${
              isDarkMode ? 'text-[#fff]' : 'text-[#000]'
            } mb-4 text-left font-lexend`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Active Challenges
          </motion.h2>
        </div>

        <div className="relative">
          <div 
            ref={sliderRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-12"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {challenges.map((challenge, index) => (
              <motion.div
                key={challenge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex-shrink-0 transition-all duration-300 overflow-hidden rounded-[30px] ${
                  currentIndex === index 
                    ? 'scale-105 shadow-[0_0_20px_rgba(255,215,0,0.3)]' 
                    : ''
                }`}
                style={{ scrollSnapAlign: 'start' }}
              >
                <ChallengeCard
                  title={challenge.title}
                  cover={challenge.coverImg}
                  amount={`$ ${challenge.reward_amount_usd}`}
                  deadline={new Date(challenge.deadline * 1000).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                  url={`https://app.lunor.quest/challenge/${challenge.id}`}
                />
              </motion.div>
            ))}
          </div>

          <div className="flex items-center gap-8 mt-8">
            <div className="flex gap-2">
              {challenges.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    currentIndex === index ? 'w-6 bg-[#FFD700]' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={prevSlide}
                disabled={currentIndex === 0}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  currentIndex === 0 
                    ? 'bg-gray-200 cursor-not-allowed' 
                    : 'bg-[#FFD700] hover:opacity-90'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                onClick={nextSlide}
                disabled={currentIndex === challenges.length - 1}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  currentIndex === challenges.length - 1
                    ? 'bg-gray-200 cursor-not-allowed'
                    : 'bg-[#FFD700] hover:opacity-90'
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActiveChallengesSection; 